import { sortFixedTermMarkets } from '@/utils/fixedTermMarkets/sortFixedTermMarkets'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { useDeferredValue, useEffect, useMemo } from 'react'
import { useFixedTermMarketsSearchState } from './useFixedTermMarketsSearchState'

export const FIXED_TERM_MARKETS_PER_PAGE = 30

export const useFixedTermMarketsList = (markets: FixedTermMarket[]) => {
  const { page, setPage, search, assets, protocols, networks, sortBy, sortOrder, setSortBy } =
    useFixedTermMarketsSearchState()
  const deferredSearch = useDeferredValue(search)
  const filteredMarkets = useMemo(
    () => filterFixedTermMarkets(markets, { search: deferredSearch, assets, protocols, networks }),
    [assets, deferredSearch, markets, networks, protocols]
  )
  const sortedMarkets = useMemo(
    () => sortFixedTermMarkets(filteredMarkets, sortBy, sortOrder),
    [filteredMarkets, sortBy, sortOrder]
  )
  const totalPages = Math.max(1, Math.ceil(sortedMarkets.length / FIXED_TERM_MARKETS_PER_PAGE))
  const currentPage = Math.min(page, totalPages)

  useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage)
    }
  }, [currentPage, page, setPage])

  const firstItemIndex = (currentPage - 1) * FIXED_TERM_MARKETS_PER_PAGE
  const pagedMarkets = sortedMarkets.slice(firstItemIndex, firstItemIndex + FIXED_TERM_MARKETS_PER_PAGE)

  return {
    filteredMarkets,
    pagedMarkets,
    currentPage,
    sortBy,
    sortOrder,
    setSortBy,
    setPage,
  } as const
}

interface FixedTermMarketFilters {
  search: string
  assets: string[]
  protocols: string[]
  networks: string[]
}

const filterFixedTermMarkets = (
  markets: FixedTermMarket[],
  { search, assets, protocols, networks }: FixedTermMarketFilters
) => {
  const normalizedSearch = search.trim().toLowerCase()
  const selectedAssets = new Set(assets.map((asset) => asset.toLowerCase()))
  const selectedProtocols = new Set(protocols.map((protocol) => protocol.toLowerCase()))
  const selectedNetworks = new Set(networks.map((network) => network.toLowerCase()))

  return markets.filter((market) => {
    const accountingAsset = market.accountingAsset.symbol?.toLowerCase()
    const underlyingProtocol = market.underlyingProtocol.name.toLowerCase()
    const network = market.network.name.toLowerCase()
    const matchesSearch =
      normalizedSearch === '' ||
      [
        market.name,
        market.accountingAsset.symbol,
        market.underlyingAsset.symbol,
        market.pt.symbol,
        market.underlyingProtocol.name,
        getNetworkName(market.network.name),
      ].some((value) => value?.toLowerCase().includes(normalizedSearch))

    return (
      matchesSearch &&
      (selectedAssets.size === 0 || (accountingAsset !== undefined && selectedAssets.has(accountingAsset))) &&
      (selectedProtocols.size === 0 || selectedProtocols.has(underlyingProtocol)) &&
      (selectedNetworks.size === 0 || selectedNetworks.has(network))
    )
  })
}
